import { useCallback, useEffect, useState, useMemo, memo } from 'react';
import jwt_decode from 'jwt-decode';
import Select from 'react-select';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button, 
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import { OrderStatus } from '../constants/statuses';
import Swal from 'sweetalert2';
import { getRequestTypes } from '../../RMVendors/Helpers/Api';
import { useValidateAndShowErrors } from '../Helpers/Validator';

const OrderedItemRepeater = memo(({
  item,
  index,
  onItemChange,
  onRemoveItem,
  isDisabled,
  isViewMode,
  selectedVendor,
  allRequestTypes = [],
}) => {
  const handleItemChange = useCallback((selectedOption) => {
    const selectedItem = selectedOption?.item;
    const updatedItem = {
      ...item,
      item_id: selectedOption ? selectedOption.value : '',
      requestsSupported: '',
      price: selectedItem?.costPerUnit || 0,
      requestedQuantity: selectedItem?.quantity?.min || 1,
    };
    onItemChange(index, updatedItem);
  }, [item, index, onItemChange]);

  const handleRequestTypeChange = useCallback((selectedOption) => {
    const updatedItem = {
      ...item,
      requestsSupported: selectedOption ? selectedOption.value : '',
    };
    onItemChange(index, updatedItem);
  }, [item, index, onItemChange]);

  const handleQuantityChange = useCallback((e) => {
    const value = parseInt(e.target.value) || 0;
    const updatedItem = {
      ...item,
      requestedQuantity: value,
    };
    onItemChange(index, updatedItem);
  }, [item, index, onItemChange]);

  const handlePriceChange = useCallback((e) => {
    const value = parseFloat(e.target.value) || 0;
    const updatedItem = {
      ...item,
      price: value,
    };
    onItemChange(index, updatedItem);
  }, [item, index, onItemChange]);

  const handleJobNameChange = useCallback((e) => {
    const updatedItem = {
      ...item,
      jobName: e.target.value,
    };
    onItemChange(index, updatedItem);
  }, [item, index, onItemChange]);

  const itemOptions = useMemo(() => {
    if (!selectedVendor || !selectedVendor.items || selectedVendor.items.length === 0) return [];

    return selectedVendor.items.map(vendorItem => ({
      value: vendorItem._id || vendorItem.sku || `${vendorItem.make}_${vendorItem.modelNo}`,
      label: `${vendorItem.description || vendorItem.make || 'Item'} ${vendorItem.modelNo ? `(${vendorItem.modelNo})` : ''}`,
      item: vendorItem,
    }));
  }, [selectedVendor]);

  const requestTypeOptions = useMemo(() => {
    if (!selectedVendor || !Array.isArray(allRequestTypes)) return [];

    const supportedRequestTypeIds = selectedVendor.requestsSupported?.map(rt =>
      typeof rt === 'object' && rt._id ? rt._id.toString() : null
    ).filter(id => id) || [];

    return allRequestTypes
      .filter(rt => supportedRequestTypeIds.includes(rt._id.toString()))
      .map(rt => ({
        value: rt._id.toString(),
        label: rt.request_type || 'Unknown Request Type',
      }))
      .filter(option => !!option.label);
  }, [selectedVendor, allRequestTypes]);

  const selectedItemDetails = useMemo(() => {
    if (!item.item_id || !selectedVendor || !selectedVendor.items) return null;
    return selectedVendor.items.find(vendorItem =>
      vendorItem._id === item.item_id ||
      vendorItem.sku === item.item_id ||
      `${vendorItem.make}_${vendorItem.modelNo}` === item.item_id
    );
  }, [item.item_id, selectedVendor]);

  const { minQuantity, maxQuantity } = useMemo(() => {
    if (!selectedItemDetails) return { minQuantity: 1, maxQuantity: undefined };

    return {
      minQuantity: selectedItemDetails.quantity?.min || 1,
      maxQuantity: selectedItemDetails.quantity?.max || undefined,
    };
  }, [selectedItemDetails]);

  const selectedItemOption = useMemo(() =>
    itemOptions.find(option => option.value === item.item_id) || null,
    [itemOptions, item.item_id]
  );

  const selectedRequestTypeOption = useMemo(() =>
    requestTypeOptions.find(option => option.value === item.requestsSupported) || null,
    [requestTypeOptions, item.requestsSupported]
  );

  // useEffect for auto-selection
  useEffect(() => {
    if (requestTypeOptions.length === 1 && !item.requestsSupported && !isDisabled && !isViewMode) {
      const singleOption = requestTypeOptions[0];
      handleRequestTypeChange(singleOption);
    }
  }, [requestTypeOptions, item.requestsSupported, isDisabled, isViewMode, handleRequestTypeChange]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      fontSize: '0.875rem',
      minHeight: '38px',
      border: state.isFocused ? '2px solid #007bff' : '1px solid #dee2e6',
      borderRadius: '0.375rem',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 123, 255, 0.25)' : 'none',
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        borderColor: state.isFocused ? '#007bff' : '#adb5bd',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '0.875rem',
      backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#6c757d',
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#495057',
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    menu: base => ({ ...base, zIndex: 9999 }),
  };

  const quantityError = useMemo(() => {
    if (!item.requestedQuantity || item.requestedQuantity < 1) {
      return 'Quantity is required and must be at least 1';
    }
    if (item.requestedQuantity < minQuantity) {
      return `Quantity must be at least ${minQuantity}`;
    }
    if (maxQuantity && item.requestedQuantity > maxQuantity) {
      return `Quantity cannot exceed ${maxQuantity}`;
    }
    return null;
  }, [item.requestedQuantity, minQuantity, maxQuantity]);

  const priceError = useMemo(() => {
    if (item.price === undefined || item.price < 0) {
      return 'Price is required and must be at least 0';
    }
    return null;
  }, [item.price]);

  const jobNameError = useMemo(() => {
    if (!item.jobName || item.jobName.trim().length === 0) {
      return 'Job name is required';
    }
    return null;
  }, [item.jobName]);

  return (
    <Card className="mb-4 shadow-sm border">
      <CardHeader className="bg-light border-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-semibold text-primary">
            <i className="fas fa-box me-2"></i>
            Item #{index + 1}
          </h6>
          {!isViewMode && (
            <Button
              color="danger"
              size="sm"
              onClick={() => onRemoveItem(index)}
              disabled={isDisabled}
              className="btn-sm"
            >
              <i className="fas fa-trash me-1"></i>
              Remove
            </Button>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-4">
        <Row className="g-3">
          <Col md={2}>
            <FormGroup>
              <Label className="form-label fw-semibold">
                <i className="fas fa-cube me-2 text-primary"></i>
                Item *
              </Label>
              <Select
                options={itemOptions}
                value={selectedItemOption}
                onChange={handleItemChange}
                isDisabled={isDisabled || !selectedVendor}
                placeholder={selectedVendor ? "Select an item" : "Select vendor first"}
                styles={customSelectStyles}
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                className={!item.item_id ? 'is-invalid' : ''}
              />
              {!item.item_id && (
                <div className="text-danger small mt-1">
                  <i className="fas fa-exclamation-circle me-1"></i>
                  Item selection is required
                </div>
              )}
              {selectedVendor && itemOptions.length === 0 && (
                <div className="text-warning small mt-1">
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  No items available for selected vendor
                </div>
              )}
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup>
              <Label className="form-label fw-semibold">
                <i className="fas fa-tags me-2 text-primary"></i>
                Request Type
              </Label>
              <Select
                options={requestTypeOptions}
                value={selectedRequestTypeOption}
                onChange={handleRequestTypeChange}
                isDisabled={isDisabled || !selectedVendor}
                placeholder={selectedVendor ? "Select request type" : "Select vendor first"}
                styles={customSelectStyles}
                isClearable
                isSearchable
                menuPortalTarget={document.body}
              />
              {selectedVendor && requestTypeOptions.length === 0 && (
                <div className="text-warning small mt-1">
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  No request types available for selected vendor
                </div>
              )}
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup>
              <Label className="form-label fw-semibold">
                <i className="fas fa-hashtag me-2 text-primary"></i>
                Quantity *
              </Label>
              <InputGroup>
                <InputGroupText>
                  <i className="fas fa-sort-numeric-up"></i>
                </InputGroupText>
                <Input
                  type="number"
                  min={minQuantity}
                  max={maxQuantity}
                  value={item.requestedQuantity || ''}
                  onChange={handleQuantityChange}
                  disabled={isDisabled}
                  invalid={!!quantityError}
                  placeholder="Enter quantity"
                  className="form-control"
                />
              </InputGroup>
              {minQuantity > 1 && (
                <div className="text-muted small mt-1">
                  <i className="fas fa-info-circle me-1"></i>
                  Min: {minQuantity}
                  {maxQuantity && `, Max: ${maxQuantity}`}
                </div>
              )}
              {quantityError && (
                <div className="text-danger small mt-1">
                  <i className="fas fa-exclamation-circle me-1"></i>
                  {quantityError}
                </div>
              )}
            </FormGroup>
          </Col>
          <Col md={2}>
            <FormGroup>
              <Label className="form-label fw-semibold">
                <i className="fas fa-dollar-sign me-2 text-primary"></i>
                Price *
              </Label>
              <InputGroup>
                <InputGroupText>$</InputGroupText>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.price || ''}
                  onChange={handlePriceChange}
                  disabled={isDisabled}
                  invalid={!!priceError}
                  placeholder="0.00"
                  className="form-control"
                />
              </InputGroup>
              {priceError && (
                <div className="text-danger small mt-1">
                  <i className="fas fa-exclamation-circle me-1"></i>
                  {priceError}
                </div>
              )}
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup>
              <Label className="form-label fw-semibold">
                <i className="fas fa-briefcase me-2 text-primary"></i>
                Job Name *
              </Label>
              <InputGroup>
                <InputGroupText>
                  <i className="fas fa-clipboard"></i>
                </InputGroupText>
                <Input
                  type="text"
                  value={item.jobName || ''}
                  onChange={handleJobNameChange}
                  disabled={isDisabled}
                  invalid={!!jobNameError}
                  placeholder="Enter job name"
                  className="form-control"
                />
              </InputGroup>
              {jobNameError && (
                <div className="text-danger small mt-1">
                  <i className="fas fa-exclamation-circle me-1"></i>
                  {jobNameError}
                </div>
              )}
            </FormGroup>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
});

OrderedItemRepeater.displayName = 'OrderedItemRepeater';

const TechnicalStaffOrderModal = memo(({ mode, open, onClose, onAdd, onEdit, formik, rmVendors = [] }) => {
  const [requestTypes, setRequestTypes] = useState([]);
  const [loadingRequestTypes, setLoadingRequestTypes] = useState(false);

  const user = useMemo(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch (error) {
      return null;
    }
  }, []);

  const isLead = useMemo(() => user?.role === 'technical staff' && user?.lead === true, [user?.role, user?.lead]);
  const validateAndShowErrors = useValidateAndShowErrors(formik);

  const fetchRequestTypes = useCallback(async () => {
    try {
      if (!user) return;
      setLoadingRequestTypes(true);
      const data = await getRequestTypes(user?.company_id || user?.id || '');
      setRequestTypes(data);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error Fetching Request Types',
        text: 'Unable to load request types. Please try again.',
        background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
        color: '#1f2937',
        iconColor: '#dc2626',
        customClass: {
          popup: 'rounded-2xl shadow-xl border-0 backdrop-blur-sm animate__animated animate__fadeIn',
          title: 'text-xl font-semibold text-gray-800 mb-3 tracking-tight',
          content: 'text-gray-600 text-base leading-relaxed',
          confirmButton: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-2.5 px-6 rounded-xl transform hover:scale-105 transition-all duration-200 shadow-md',
          icon: 'border-4 border-red-100 rounded-full p-2',
          closeButton: 'text-gray-400 hover:text-red-600 transition-colors duration-200'
        },
        confirmButtonText: '<i class="fas fa-check me-2"></i> Okay',
        buttonsStyling: false,
        showCloseButton: true,
        timer: 6000,
        timerProgressBar: true,
        backdrop: 'rgba(220, 38, 38, 0.3)',
        didOpen: () => {
          const popup = Swal.getPopup();
          popup.style.animation = 'fadeInUp 0.3s ease-out';
        }
      });
    } finally {
      setLoadingRequestTypes(false);
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      fetchRequestTypes();
    }
  }, [open, fetchRequestTypes]);

  const vendorSelectOptions = useMemo(() =>
    rmVendors.map(vendor => ({
      value: vendor._id,
      label: `${vendor.companyName} (${vendor.contactName})`,
    })), [rmVendors]);

  const selectedVendor = useMemo(() =>
    rmVendors.find(vendor => vendor._id === formik.values.vendorId), [rmVendors, formik.values.vendorId]);

  const selectedVendorOption = useMemo(() =>
    vendorSelectOptions.find(option => option.value === formik.values.vendorId) || null, [vendorSelectOptions, formik.values.vendorId]);

  const totalPrice = useMemo(() => {
    if (!formik.values.orderedItems || !Array.isArray(formik.values.orderedItems)) return 0;
    return formik.values.orderedItems.reduce((total, item) => {
      const itemPrice = parseFloat(item.price) || 0;
      const itemQuantity = parseInt(item.requestedQuantity) || 0;
      return total + (itemPrice * itemQuantity);
    }, 0);
  }, [formik.values.orderedItems]);

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      fontSize: '0.875rem',
      minHeight: '38px',
      border: state.isFocused ? '2px solid #3b82f6' : '1px solid #e2e8f0',
      borderRadius: '0.5rem',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
      transition: 'all 0.2s ease-in-out',
      '&:hover': { borderColor: state.isFocused ? '#3b82f6' : '#cbd5e1' },
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '0.875rem',
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f1f5f9' : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      '&:hover': { backgroundColor: state.isSelected ? '#3b82f6' : '#f1f5f9' },
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#6b7280',
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#1f2937',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e5e7eb',
      borderRadius: '6px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#1f2937',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#dc2626',
      ':hover': {
        backgroundColor: '#dc2626',
        color: 'white',
      },
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    menu: base => ({ ...base, zIndex: 9999 }),
  };

  const generateOrderReview = useCallback(() => {
    const vendor = selectedVendor;
    const items = formik.values.orderedItems || [];
    const comments = formik.values.comments || '';
    const vendorName = vendor ? `${vendor.companyName} (${vendor.contactName})` : 'Unknown Vendor';

    let itemsHtml = '';
    let totalQuantity = 0;
    let grandTotal = 0;

    if (items.length > 0) {
      itemsHtml = items.map((item, index) => {
        const itemPrice = parseFloat(item.price) || 0;
        const itemQuantity = parseInt(item.requestedQuantity) || 0;
        const itemTotal = itemPrice * itemQuantity;

        totalQuantity += itemQuantity;
        grandTotal += itemTotal;

        const itemDetails = selectedVendor?.items?.find(vendorItem =>
          vendorItem._id === item.item_id || vendorItem.sku === item.item_id || `${vendorItem.make}_${vendorItem.modelNo}` === item.item_id
        );
        const requestTypeDetails = requestTypes.find(rt => rt._id.toString() === item.requestsSupported);

        return `
          <tr style="border-bottom: 1px solid #e5e7eb; background-color: ${index % 2 === 0 ? '#ffffff' : '#f9fafb'}; transition: background-color 0.2s ease;">
            <td style="padding: 16px; text-align: left; vertical-align: middle; font-weight: 500; color: #1f2937;">
              <i class="fas fa-hashtag" style="margin-right: 6px; color: #6b7280;"></i>${index + 1}
            </td>
            <td style="padding: 16px; text-align: left; vertical-align: middle;">
              <div style="font-size: 0.95rem; font-weight: 600; color: #1e293b; margin-bottom: 6px;">
                <i class="fas fa-briefcase" style="margin-right: 6px; color: #1e3a8a;"></i>${item.jobName || 'N/A'}
              </div>
              ${itemDetails ? `
                <div style="font-size: 0.85rem; color: #4b5563; margin-bottom: 4px;">
                  <i class="fas fa-box" style="margin-right: 6px; color: #6b7280;"></i>
                  Item: ${itemDetails.description || itemDetails.make || 'N/A'} ${itemDetails.modelNo ? `(Model: ${itemDetails.modelNo})` : ''}
                </div>
                ${itemDetails.sku ? `
                  <div style="font-size: 0.85rem; color: #4b5563;">
                    <i class="fas fa-barcode" style="margin-right: 6px; color: #6b7280;"></i>
                    SKU: ${itemDetails.sku}
                  </div>` : ''}
                ${itemDetails.make ? `
                  <div style="font-size: 0.85rem; color: #4b5563;">
                    <i class="fas fa-industry" style="margin-right: 6px; color: #6b7280;"></i>
                    Make: ${itemDetails.make}
                  </div>` : ''}
                ${itemDetails.description ? `
                  <div style="font-size: 0.85rem; color: #4b5563;">
                    <i class="fas fa-align-left" style="margin-right: 6px; color: #6b7280;"></i>
                    Description: ${itemDetails.description}
                  </div>` : ''}
              ` : `
                <div style="font-size: 0.85rem; color: #6b7280; font-style: italic;">
                  <i class="fas fa-exclamation-circle" style="margin-right: 6px; color: #ef4444;"></i>
                  Item details not available
                </div>`}
              ${requestTypeDetails ? `
                <div style="font-size: 0.85rem; color: #4b5563;">
                  <i class="fas fa-folder" style="margin-right: 6px; color: #6b7280;"></i>
                  Request Type: ${requestTypeDetails.request_type}
                </div>` : ''}
            </td>
            <td style="padding: 16px; text-align: center; vertical-align: middle; font-weight: 500; color: #1f2937;">
              <i class="fas fa-sort-numeric-up" style="margin-right: 6px; color: #6b7280;"></i>${itemQuantity}
              ${itemDetails?.quantity ? `
                <div style="font-size: 0.8rem; color: #6b7280; margin-top: 4px;">
                  ${itemDetails.quantity.min ? `Min: ${itemDetails.quantity.min}` : ''}${itemDetails.quantity.max ? `${itemDetails.quantity.min ? ', ' : ''}Max: ${itemDetails.quantity.max}` : ''}
                </div>` : ''}
            </td>
            <td style="padding: 16px; text-align: right; vertical-align: middle; font-weight: 500; color: #1f2937;">
              <i class="fas fa-dollar-sign" style="margin-right: 6px; color: #16a34a;"></i>$${itemPrice.toFixed(2)}
              ${itemDetails?.costPerUnit && itemDetails.costPerUnit !== itemPrice ? `
                <div style="font-size: 0.8rem; color: #6b7280; margin-top: 4px;">
                  (List: $${itemDetails.costPerUnit.toFixed(2)})
                </div>` : ''}
            </td>
            <td style="padding: 16px; text-align: right; vertical-align: middle; font-weight: 600; color: #3b82f6;">
              <i class="fas fa-receipt" style="margin-right: 6px;"></i>$${itemTotal.toFixed(2)}
            </td>
          </tr>
        `;
      }).join('');

      itemsHtml += `
        <tr style="border-top: 2px solid #3b82f6; background-color: #f0f9ff; font-weight: 700;">
          <td style="padding: 16px; text-align: left; vertical-align: middle; color: #1e293b;">
            <i class="fas fa-sigma" style="margin-right: 6px; color: #1e3a8a;"></i>
          </td>
          <td style="padding: 16px; text-align: left; vertical-align: middle; color: #1e293b;"></td>
          <td style="padding: 16px; text-align: center; vertical-align: middle; color: #1e293b;">
            ${totalQuantity}
          </td>
          <td style="padding: 16px; text-align: right; vertical-align: middle; color: #1e293b;"></td>
          <td style="padding: 16px; text-align: right; vertical-align: middle; color: #1e293b;">
            $${grandTotal.toFixed(2)}
          </td>
        </tr>
      `;
    } else {
      itemsHtml = '<tr><td colspan="5" style="padding: 24px; text-align: center; color: #6b7280; font-size: 0.95rem;">No items added</td></tr>';
    }

    const formatAddress = (address) => {
      if (!address) return '';
      const parts = [address.street, address.city, address.province, address.postalCode, address.country].filter(part => part && part.trim());
      return parts.join(', ');
    };

    const formatPhone = (phone) => {
      if (!phone) return '';
      return `${phone.countryCode} ${phone.number}`;
    };

    return `
      <div style="text-align: left; padding: 24px; max-height: 80vh; overflow-y: auto; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
        <h3 style="color: #1e293b; margin-bottom: 12px; border-bottom: 3px solid #3b82f6; padding-bottom: 6px; font-size: 1rem; font-weight: 600; letter-spacing: -0.025em;">
          Order Review
        </h3>
        <div style="background-color: #f9fafb; padding: 10px; border-radius: 12px; margin-bottom: 24px; border: 1px solid #e5e7eb; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
          <div style="font-size: 0.85rem; color: #1e293b; line-height: 1.4;">
            ${[
        vendorName ? `You're ordering from <span class="text-dark fw-bold">${vendorName}</span>` : '',
        vendor?.email ? `you can reach them at <span class="text-dark fw-bold">${vendor.email}</span>` : '',
        vendor?.phone ? `their phone number is <span class="text-dark fw-bold">${formatPhone(vendor.phone)}</span>` : '',
        vendor?.address ? `their address is <span class="text-dark fw-bold">${formatAddress(vendor.address)}</span>` : '',
        comments ? `including these notes: <em>${comments}</em>` : ''
      ]
        .filter(Boolean)
        .join('; ')
      }.
          </div>
        </div>
        <table style="width: 100%; 
                      border-collapse: separate; 
                      border-spacing: 0;
                      background-color: #ffffff;
                      border-radius: 12px;
                      overflow: hidden;
                      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                      font-family: 'Arial', sans-serif;
                    ">
          <thead style="
            background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
            color: #ffffff;
            position: relative;
          ">
            <tr style="position: relative;">
              <th class="text-primary" style="
                padding: 12px 16px !important;
                text-align: left;
                font-weight: 700;
                font-size: 0.85rem;
                letter-spacing: 0.6px;
                border-right: 1px solid rgba(255, 255, 255, 0.2);
                width: 8% !important;
                background: linear-gradient(90deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0));
                transition: background 0.3s ease;
              ">
                <i class="fas fa-list-ol" style="margin-right: 8px; opacity: 0.9;"></i>
                No.
              </th>
              <th class="text-primary" style="
                padding: 12px 16px !important;
                text-align: left;
                font-weight: 700;
                font-size: 0.85rem;
                letter-spacing: 0.6px;
                border-right: 1px solid rgba(255, 255, 255, 0.2);
                width: 42% !important;
                background: linear-gradient(90deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0));
                transition: background 0.3s ease;
              ">
                <i class="fas fa-cube" style="margin-right: 8px; opacity: 0.9;"></i>
                Item Details
              </th>
              <th class="text-primary" style="
                padding: 12px 16px !important;
                text-align: center;
                font-weight: 700;
                font-size: 0.85rem;
                letter-spacing: 0.6px;
                border-right: 1px solid rgba(255, 255, 255, 0.2);
                width: 18% !important;
                background: linear-gradient(90deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0));
                transition: background 0.3s ease;
              ">
                <i class="fas fa-calculator" style="margin-right: 8px; opacity: 0.9;"></i>
                Quantity
              </th>
              <th class="text-primary" style="
                padding: 12px 16px !important;
                text-align: right;
                font-weight: 700;
                font-size: 0.85rem;
                letter-spacing: 0.6px;
                border-right: 1px solid rgba(255, 255, 255, 0.2);
                width: 16% !important;
                background: linear-gradient(90deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0));
                transition: background 0.3s ease;
              ">
                <i class="fas fa-tag" style="margin-right: 8px; opacity: 0.9;"></i>
                Unit Price
              </th>
              <th class="text-primary" style="
                padding: 12px 16px !important;
                text-align: right;
                font-weight: 700;
                font-size: 0.85rem;
                letter-spacing: 0.6px;
                width: 16% !important;
                background: linear-gradient(90deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0));
                transition: background 0.3s ease;
              ">
                <i class="fas fa-money-bill-wave" style="margin-right: 8px; opacity: 0.9;"></i>
                Total
              </th>
            </tr>
          </thead>
          <tbody style="
            background-color: #f9fafb;
          ">
            <tr style="
              transition: background 0.2s ease;
            ">
              ${itemsHtml}
            </tr>
          </tbody>
        </table>
        <div style="padding: 16px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border: 1px solid #fcd34d; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <p style="margin: 0; font-size: 0.95rem; color: #5a2d0c; line-height: 1.6; display: flex; align-items: center; gap: 8px;">
            <i class="fas fa-info-circle" style="font-size: 0.95rem; color: #5a2d0c;"></i>
            <span>Note:</span> Please review all item details carefully before confirming your order. Contact the vendor directly for any questions.</span>
          </p>
        </div>
      </div>
    `;
  }, [selectedVendor, formik.values.orderedItems, formik.values.comments, requestTypes, totalPrice]);

  const showOrderReview = useCallback(async (actionTitle) => {
    const reviewHtml = generateOrderReview();

    const result = await Swal.fire({
      title: `
        <div class="d-flex align-items-center justify-content-center gap-2 mb-3">
          <i class="fas fa-clipboard-check text-primary fs-4"></i>
          <span class="fw-bold text-primary">Order Review</span>
        </div>
      `.trim(),
      html: `<div class="container-fluid p-0">
            <div class="alert alert-info border-0 rounded-3 mb-2 py-2 px-3" style="font-size: 0.85rem; background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);">
              <div class="d-flex align-items-center p-2">
                <i class="fas fa-info-circle text-info me-2 fs-6"></i>
                <small class="mb-0 fw-medium">
                  Please review your order details before proceeding with 
                  <span class="text-dark fw-bold">${actionTitle}</span>
                </small>
              </div>
            </div>
            <div class="card border-0 shadow-sm rounded-3">
              <div class="card-body p-2" style="max-height: 400px; overflow-y: auto; font-size: 0.85rem;">
                ${reviewHtml}
              </div>
            </div>
            <div class="mt-2 p-2 bg-light rounded-3">
              <div class="d-flex align-items-center justify-content-center text-muted" style="font-size: 0.75rem;">
                <i class="fas fa-shield-alt me-2 fs-6"></i>
                <small>Your order information is secure and encrypted</small>
              </div>
            </div>
          </div>`,
      icon: null,
      background: '#ffffff',
      color: '#212529',
      showCancelButton: true,
      confirmButtonColor: '#0d6efd',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `
        <i class="fas fa-rocket me-2"></i>
        <span class="fw-semibold">Yes, ${actionTitle}</span>
      `,
      cancelButtonText: `
        <i class="fas fa-times me-2"></i>
        <span>Cancel</span>
      `,
      width: '90%',
      maxWidth: '800px',
      padding: '1.5rem',
      customClass: {
        popup: `
          rounded-4 shadow-lg border-0 position-relative
          animate__animated animate__fadeInUp animate__faster
        `,
        title: 'border-bottom pb-3 mb-0',
        htmlContainer: 'text-start p-0 mt-3',
        confirmButton: `
          btn btn-primary btn-lg rounded-pill px-4 py-2 fw-semibold
          shadow-sm border-0 position-relative overflow-hidden
          hover-lift
        `,
        cancelButton: `
          btn btn-secondary btn-lg rounded-pill px-4 py-2 fw-semibold
          shadow-sm border-0
          hover-lift
        `,
        actions: 'gap-3 mt-4 pt-3 border-top',
        closeButton: 'btn-close btn-close-white position-absolute top-0 end-0 m-3'
      },
      buttonsStyling: false,
      allowOutsideClick: false,
      allowEscapeKey: true,
      focusConfirm: true,
      reverseButtons: true,
      didOpen: () => {
        const popup = Swal.getPopup();
        const style = document.createElement('style');
        style.textContent = `
          .hover-lift {
            transition: all 0.2s ease-in-out !important;
          }
          .hover-lift:hover {
            transform: translateY(-2px) !important;
            box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
          }
          .swal2-popup {
            border: 1px solid rgba(0,0,0,0.1) !important;
          }
          .card-body::-webkit-scrollbar {
            width: 6px;
          }
          .card-body::-webkit-scrollbar-track {
            background: #f8f9fa;
            border-radius: 10px;
          }
          .card-body::-webkit-scrollbar-thumb {
            background: #dee2e6;
            border-radius: 10px;
          }
          .card-body::-webkit-scrollbar-thumb:hover {
            background: #ced4da;
          }
          .btn-primary::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(45deg, transparent, rgba(255,255,255,0.1), transparent);
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }
          .btn-primary:hover::before {
            transform: translateX(100%);
          }
          @media (max-width: 576px) {
            .swal2-popup {
              width: 95% !important;
              margin: 0.5rem !important;
              padding: 1rem !important;
            }
            .btn-lg {
              font-size: 0.9rem !important;
              padding: 0.5rem 1.5rem !important;
            }
          }
        `;
        document.head.appendChild(style);
        popup.style.opacity = '0';
        popup.style.transform = 'translateY(30px) scale(0.95)';
        requestAnimationFrame(() => {
          popup.style.transition = 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)';
          popup.style.opacity = '1';
          popup.style.transform = 'translateY(0) scale(1)';
        });
      },
      didClose: () => {
        const customStyles = document.querySelectorAll('style');
        customStyles.forEach(style => {
          if (style.textContent.includes('.hover-lift')) {
            style.remove();
          }
        });
      }
    });

    return result.isConfirmed;
  }, [generateOrderReview]);

  const handleAddItem = useCallback(() => {
    const newItem = { item_id: '', requestsSupported: '', requestedQuantity: 1, price: 0, jobName: '' };
    const updatedItems = [...(formik.values.orderedItems || []), newItem];
    formik.setFieldValue('orderedItems', updatedItems);
  }, [formik]);

  const handleItemChange = useCallback((index, updatedItem) => {
    const updatedItems = [...(formik.values.orderedItems || [])];
    updatedItems[index] = updatedItem;
    formik.setFieldValue('orderedItems', updatedItems);
  }, [formik]);

  const handleRemoveItem = useCallback((index) => {
    const updatedItems = [...(formik.values.orderedItems || [])];
    updatedItems.splice(index, 1);
    formik.setFieldValue('orderedItems', updatedItems);
  }, [formik]);

  const handleVendorChange = useCallback((selectedOption) => {
    formik.setFieldValue('vendorId', selectedOption ? selectedOption.value : '');
    formik.setFieldValue('orderedItems', []);
  }, [formik]);

  const handleDraft = useCallback(async () => {
    const isValid = await validateAndShowErrors();
    if (!isValid) return;
    const confirmed = await showOrderReview('Save as Draft');
    if (!confirmed) return;
    formik.setFieldValue('status', OrderStatus.DRAFT);
    if (mode === 'add') {
      onAdd?.(formik.values, { setSubmitting: formik.setSubmitting });
    } else if (mode === 'edit') {
      onEdit?.(formik.values, { setSubmitting: formik.setSubmitting });
      onClose();
    }
  }, [formik, mode, onAdd, onEdit, onClose, showOrderReview, validateAndShowErrors]);

  const handleSubmitOrder = useCallback(async () => {
    const isValid = await validateAndShowErrors();
    if (!isValid) return;

    const newStatus = isLead ? OrderStatus.APPROVED : OrderStatus.SUBMITTED;

    const confirmed = await showOrderReview(
      isLead ? 'Approve for Delivery' : 'Submit Order'
    );
    if (!confirmed) return;

    formik.setFieldValue('status', newStatus);

    const updatedValues = {
      ...formik.values,
      status: newStatus,
    };

    if (mode === 'add') {
      onAdd?.(updatedValues, { setSubmitting: formik.setSubmitting });
    } else if (mode === 'edit') {
      onEdit?.(updatedValues, { setSubmitting: formik.setSubmitting });
      onClose();
    }
  }, [formik, mode, onAdd, onEdit, onClose, isLead, showOrderReview, validateAndShowErrors]);

  const handleReject = useCallback(async () => {
    const confirmed = await Swal.fire({
      title: `
        <div class="d-flex align-items-center justify-content-center gap-2 mb-3">
          <i class="fas fa-times-circle text-danger fs-4"></i>
          <span class="fw-bold text-danger">Reject Order</span>
        </div>
      `.trim(),
      html: `
        <div class="container-fluid p-0">
          <div class="alert alert-warning border-0 rounded-3 mb-2 py-2 px-3" style="font-size: 0.85rem; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);">
            <div class="d-flex align-items-center p-2">
              <i class="fas fa-exclamation-triangle text-warning me-2 fs-6"></i>
              <small class="mb-0 fw-medium">
                Are you sure you want to reject this order?
              </small>
            </div>
          </div>
        </div>
      `,
      icon: null,
      background: '#ffffff',
      color: '#212529',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `
        <i class="fas fa-times me-2"></i>
        <span class="fw-semibold">Yes, Reject</span>
      `,
      cancelButtonText: `
        <i class="fas fa-times me-2"></i>
        <span>Cancel</span>
      `,
      width: '500px',
      padding: '1.5rem',
      customClass: {
        popup: `
          rounded-4 shadow-lg border-0 position-relative
          animate__animated animate__fadeInUp animate__faster
        `,
        title: 'border-bottom pb-3 mb-0',
        htmlContainer: 'text-start p-0 mt-3',
        confirmButton: `
          btn btn-danger btn-lg rounded-pill px-4 py-2 fw-semibold
          shadow-sm border-0 position-relative overflow-hidden
          hover-lift
        `,
        cancelButton: `
          btn btn-secondary btn-lg rounded-pill px-4 py-2 fw-semibold
          shadow-sm border-0
          hover-lift
        `,
        actions: 'gap-3 mt-4 pt-3 border-top',
        closeButton: 'btn-close btn-close-white position-absolute top-0 end-0 m-3'
      },
      buttonsStyling: false,
      allowOutsideClick: false,
      allowEscapeKey: true,
      focusConfirm: true,
      reverseButtons: true,
    });

    if (confirmed.isConfirmed) {
      formik.setFieldValue('status', OrderStatus.REJECTED);
      if (mode === 'edit') {
        onEdit?.(formik.values, { setSubmitting: formik.setSubmitting });
        onClose();
      }
    }
  }, [formik, mode, onEdit, onClose]);

  const isViewMode = useMemo(() => mode === 'view', [mode]);
  const isDisabled = useMemo(() => isViewMode || formik.isSubmitting, [isViewMode, formik.isSubmitting]);

  if (!open) return null;

  return (
    <Modal isOpen={open} toggle={onClose} size="xl" style={{ maxWidth: '95%', width: '1200px' }} className="order-modal shadow-lg" backdrop="static">
      <ModalHeader
        toggle={onClose}
        className="bg-primary text-white d-flex align-items-center"
        close={<button className="btn-close btn-close-white" onClick={onClose}></button>}
      >
        <i className="fas fa-shopping-cart me-2 text-white"></i>
        <span className="text-white">
          {mode === 'edit'
            ? 'Edit Order'
            : mode === 'view'
              ? 'Order Details'
              : 'Create New Inventory Order'}
        </span>
      </ModalHeader>

      <ModalBody style={{ backgroundColor: '#f8f9fa', padding: '2rem' }}>
        <Form>
          <Card className="mb-4 shadow-sm border">
            <CardHeader className="bg-light border-bottom">
              <h6 className="mb-0 fw-semibold text-primary">
                <i className="fas fa-info-circle me-2"></i>Order Information
              </h6>
            </CardHeader>
            <CardBody className="p-4">
              <Row className="g-3">
                <Col md={4}>
                  <FormGroup>
                    <Label className="form-label fw-semibold">
                      <i className="fas fa-building me-2 text-primary"></i>Vendor *
                    </Label>
                    <Select
                      id="vendorId"
                      name="vendorId"
                      options={vendorSelectOptions}
                      value={selectedVendorOption}
                      onChange={handleVendorChange}
                      onBlur={() => formik.setFieldTouched('vendorId', true)}
                      isDisabled={isDisabled}
                      placeholder="Select a vendor"
                      styles={customSelectStyles}
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      className={formik.touched.vendorId && formik.errors.vendorId ? 'is-invalid' : ''}
                    />
                    {formik.touched.vendorId && formik.errors.vendorId && (
                      <div className="text-danger small mt-1">
                        <i className="fas fa-exclamation-circle me-1"></i>
                        {formik.errors.vendorId}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={8}>
                  <FormGroup>
                    <Label className="form-label fw-semibold">
                      <i className="fas fa-comment me-2 text-primary"></i>Comments
                    </Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="fas fa-comment-dots"></i>
                      </InputGroupText>
                      <Input
                        name="comments"
                        id="comments"
                        value={formik.values.comments || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={isDisabled}
                        invalid={formik.touched.comments && formik.errors.comments}
                        placeholder="Add any additional comments or notes..."
                      />
                    </InputGroup>
                    {formik.touched.comments && formik.errors.comments && (
                      <div className="text-danger small mt-1">
                        <i className="fas fa-exclamation-circle me-1"></i>
                        {formik.errors.comments}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="mb-4 shadow-sm border">
            <CardHeader className="bg-light border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-semibold text-primary">
                  <i className="fas fa-boxes me-2"></i>Order Items
                  {loadingRequestTypes && (
                    <span className="ms-2 text-muted" style={{ fontSize: '0.875rem' }}>
                      (Loading request types...)
                    </span>
                  )}
                </h6>
                {!isViewMode && (
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleAddItem}
                      disabled={isDisabled || !selectedVendor || loadingRequestTypes}
                      className="btn-sm"
                      style={{ borderRadius: '6px', padding: '0.5rem 1rem' }}
                    >
                      <i className="fas fa-plus me-1"></i>Add Item
                    </Button>
                    {selectedVendor && !loadingRequestTypes && formik.values.orderedItems?.length > 0 && (
                      <>
                        <span className="mx-2 text-muted">|</span>
                        <h6 className="mb-0 fw-semibold text-primary">
                          <i className="fas fa-dollar-sign me-1"></i>
                          Total: <span className="text-dark">${totalPrice.toFixed(2)}</span>
                        </h6>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardBody className="p-4">
              {formik.touched.orderedItems && formik.errors.orderedItems && typeof formik.errors.orderedItems === 'string' && (
                <div className="mb-3">
                  <div className="text-danger small">
                    <i className="fas fa-exclamation-circle me-1"></i>
                    {formik.errors.orderedItems}
                  </div>
                </div>
              )}
              {!selectedVendor && (
                <div className="text-center text-muted" style={{ padding: '2rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                  <p className="mb-0">Please select a vendor first to add items to your order.</p>
                </div>
              )}
              {selectedVendor && loadingRequestTypes && (
                <div className="text-center text-muted" style={{ padding: '2rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                  <p className="mb-0">Loading request types...</p>
                </div>
              )}
              {selectedVendor && !loadingRequestTypes && (!formik.values.orderedItems || formik.values.orderedItems.length === 0) && (
                <div className="text-center text-muted" style={{ padding: '2rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                  <p className="mb-0">No items added yet. Click "Add Item" to start building your order.</p>
                </div>
              )}
              {selectedVendor && !loadingRequestTypes && Array.isArray(formik.values.orderedItems) && formik.values.orderedItems.length > 0 && (
                <div>
                  {formik.values.orderedItems.map((item, index) => (
                    <OrderedItemRepeater
                      key={index}
                      item={item}
                      index={index}
                      onItemChange={handleItemChange}
                      onRemoveItem={handleRemoveItem}
                      isDisabled={isDisabled}
                      isViewMode={isViewMode}
                      selectedVendor={selectedVendor}
                      allRequestTypes={requestTypes}
                    />
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </Form>
      </ModalBody>
      <ModalFooter className="bg-light border-top">
        {isLead && formik.values.status === OrderStatus.SUBMITTED ? (
          <>
            <Button
              color="danger"
              onClick={handleReject}
              disabled={formik.isSubmitting || loadingRequestTypes}
              style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '6px', marginRight: '1rem', transition: 'all 0.2s' }}
              className="hover:bg-red-700"
            >
              <i className="fas fa-times me-1"></i>
              {formik.isSubmitting ? 'Rejecting...' : 'Reject'}
            </Button>
            <Button
              color="primary"
              onClick={handleSubmitOrder}
              disabled={formik.isSubmitting || loadingRequestTypes}
              style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '6px', transition: 'all 0.2s' }}
              className="hover:bg-blue-700"
            >
              <i className="fas fa-check me-1"></i>
              {formik.isSubmitting ? 'Approving...' : 'Approve'}
            </Button>
          </>
        ) : (
          <>
            <Button
              color="secondary"
              outline
              onClick={onClose}
              disabled={formik.isSubmitting}
              style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '6px', marginRight: '1rem', transition: 'all 0.2s' }}
              className="hover:bg-gray-100"
            >
              <i className="fas fa-times me-1"></i>{isViewMode ? 'Close' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <>
                <Button
                  color="secondary"
                  onClick={handleDraft}
                  disabled={formik.isSubmitting || loadingRequestTypes}
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '6px', marginRight: '1rem', transition: 'all 0.2s' }}
                  className="hover:bg-gray-200"
                >
                  <i className="fas fa-save me-1"></i>
                  {formik.isSubmitting ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button
                  color="primary"
                  onClick={handleSubmitOrder}
                  disabled={formik.isSubmitting || loadingRequestTypes}
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1.5rem', borderRadius: '6px', transition: 'all 0.2s' }}
                  className="hover:bg-blue-700"
                >
                  <i className="fas fa-check me-1"></i>
                  {formik.isSubmitting
                    ? (isLead ? 'Approving...' : 'Submitting...')
                    : (isLead ? 'Approve for Delivery' : 'Submit for Approval')
                  }
                </Button>
              </>
            )}
          </>
        )}
      </ModalFooter>
    </Modal>
  );
});

TechnicalStaffOrderModal.displayName = 'TechnicalStaffOrderModal';

export { TechnicalStaffOrderModal };